import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Search, Copy, X, Syringe } from "lucide-react";
import { Input } from "./ui/input";

const SITES = ["Abdomen", "Thigh", "Upper arm", "Buttock"];

function calcCartridges(tdd, durationDays, unitsPerCartridge = 300) {
  if (!tdd || !durationDays) return "";
  const total = tdd * durationDays;
  return Math.ceil(total / unitsPerCartridge);
}
function durDays(s) {
  const m = String(s || "").match(/(\d+)\s*(day|week|month)/i);
  if (!m) return 0;
  const n = parseInt(m[1]); const u = m[2].toLowerCase();
  if (u.startsWith("day")) return n;
  if (u.startsWith("week")) return n * 7;
  if (u.startsWith("month")) return n * 30;
  return 0;
}

export default function InsulinRow({ row, index, onChange, onDuplicate, onDelete }) {
  const [q, setQ] = useState(row.brand_name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const t = useRef();

  useEffect(() => { setQ(row.brand_name || ""); }, [row.brand_name]);

  const doSearch = (val) => {
    setQ(val); onChange({ ...row, brand_name: val });
    clearTimeout(t.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    t.current = setTimeout(async () => {
      try {
        const r = await api.get(`/medicines/search?q=${encodeURIComponent(val)}`);
        // NOTE: Broad search is intentional here (doctor may want to grab the
        // right long-acting formulation across brand + generic + composition);
        // filter narrowed if we later need insulin-only.
        setResults(r.data.results);
        setOpen(true);
      } catch (e) { console.debug("[silent]", e); }
    }, 350);
  };

  const pick = (m) => {
    onChange({
      ...row,
      medicine_id: m.medicine_id, brand_name: m.brand_name, generic_name: m.generic_name,
      composition: m.composition, strength: m.strength, dosage_form: m.dosage_form || "Injection",
      route: "SC",
    });
    setQ(m.brand_name); setOpen(false);
  };

  const regimen = row.insulin_regimen || { morning: "", afternoon: "", evening: "", bedtime: "", site: "Abdomen" };
  const setReg = (k, v) => {
    const next = { ...regimen, [k]: v };
    const tdd = ["morning", "afternoon", "evening", "bedtime"].reduce((s, key) => s + (parseFloat(next[key]) || 0), 0);
    const dd = durDays(row.duration);
    onChange({
      ...row,
      insulin_regimen: next,
      dose: `${tdd || 0} U/day`,
      frequency: "As per regimen",
      quantity: calcCartridges(tdd, dd) || row.quantity,
    });
  };

  const tdd = ["morning", "afternoon", "evening", "bedtime"].reduce((s, k) => s + (parseFloat(regimen[k]) || 0), 0);

  return (
    <tr className="border-b border-slate-100 bg-amber-50/40 align-top" data-testid={`insulin-row-${index}`}>
      <td className="p-2 text-center text-xs text-slate-500 tabular w-8 sticky left-0 z-10 bg-amber-50">
        <div className="inline-flex items-center justify-center h-5 w-5 rounded bg-amber-500 text-white">
          <Syringe size={11} />
        </div>
      </td>
      <td className="p-2 relative min-w-[200px] sticky left-8 z-10 bg-amber-50 border-r border-slate-100">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
          <Input
            data-testid={`insulin-medicine-input-${index}`}
            value={q}
            onChange={(e) => doSearch(e.target.value)}
            onFocus={() => q.length >= 2 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder="Insulin brand (e.g. Lantus, NovoMix)…"
            className="h-8 pl-7 text-sm"
          />
        </div>
        {open && results.length > 0 && (
          <div className="absolute z-30 left-2 right-2 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto">
            {results.map((m) => (
              <button key={m.medicine_id} onMouseDown={() => pick(m)} className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50 border-b border-slate-100" data-testid={`insulin-option-${m.medicine_id}`}>
                <div className="font-medium">{m.brand_name} {m.strength}</div>
                <div className="text-slate-500">{m.generic_name}</div>
              </button>
            ))}
          </div>
        )}
        <div className="text-[10px] font-medium text-amber-700 mt-1">INSULIN · SC</div>
      </td>
      <td className="p-2" colSpan={5}>
        <div className="grid grid-cols-5 gap-2">
          <UnitField label="Morning" val={regimen.morning} onChange={(v) => setReg("morning", v)} testid={`ins-morning-${index}`} />
          <UnitField label="Afternoon" val={regimen.afternoon} onChange={(v) => setReg("afternoon", v)} testid={`ins-afternoon-${index}`} />
          <UnitField label="Evening" val={regimen.evening} onChange={(v) => setReg("evening", v)} testid={`ins-evening-${index}`} />
          <UnitField label="Bedtime" val={regimen.bedtime} onChange={(v) => setReg("bedtime", v)} testid={`ins-bedtime-${index}`} />
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">TDD</div>
            <div className="mt-1 h-8 flex items-center justify-center rounded bg-amber-100 text-amber-800 font-semibold tabular text-sm" data-testid={`ins-tdd-${index}`}>{tdd || 0} U</div>
          </div>
          <div className="col-span-3">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Site rotation</div>
            <select
              value={regimen.site || "Abdomen"}
              onChange={(e) => setReg("site", e.target.value)}
              className="mt-1 h-8 w-full border border-slate-200 rounded px-2 text-sm"
              data-testid={`ins-site-${index}`}
            >
              {SITES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Duration</div>
            <Input
              value={row.duration || ""}
              onChange={(e) => {
                const newDur = e.target.value;
                onChange({ ...row, duration: newDur, quantity: calcCartridges(tdd, durDays(newDur)) || row.quantity });
              }}
              className="h-8 text-sm mt-1"
              placeholder="1 month"
              data-testid={`ins-duration-${index}`}
            />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Cartridges</div>
            <div className="mt-1 h-8 flex items-center justify-center rounded bg-slate-100 text-slate-700 tabular text-sm" data-testid={`ins-cartridges-${index}`}>
              {calcCartridges(tdd, durDays(row.duration)) || "—"}
            </div>
          </div>
        </div>
      </td>
      <td className="p-2">
        <Input value={row.instructions || ""} onChange={(e) => onChange({ ...row, instructions: e.target.value })} placeholder="e.g. Rotate sites; check sugar before dose" className="h-8 text-sm" />
      </td>
      <td className="p-2">
        <div className="flex gap-1">
          <button onClick={onDuplicate} className="p-1 rounded hover:bg-slate-100" title="Duplicate"><Copy size={14} /></button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`insulin-delete-${index}`}><X size={14} /></button>
        </div>
      </td>
    </tr>
  );
}

function UnitField({ label, val, onChange, testid }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <Input type="number" min="0" value={val || ""} onChange={(e) => onChange(e.target.value)} placeholder="U" className="h-8 text-sm mt-1 tabular text-center" data-testid={testid} />
    </div>
  );
}
